import { z } from 'zod';
import { CalendarDate } from './project.js';
/**
 * Phương án tiếp quản của người nghỉ việc — APM-OFFB-c (người dùng chốt 2026-10-01).
 *
 * Hai nhóm: **Tệp của tôi** (tệp + thư mục cá nhân, kèm link/quyền chia sẻ) và
 * **Người dưới quyền** (ai đang có người nghỉ làm quản lý trực tiếp). Task không
 * nằm ở đây — việc trong dự án do PM chuyển.
 */
export const OFFBOARDING_CATEGORIES = ['my_files', 'direct_reports'];
export const OffboardingCategory = z.enum(OFFBOARDING_CATEGORIES);
/**
 * `unplanned` = chưa ai quyết; `planned` = đã chọn, chờ chạy (khi tài khoản khoá
 * hoặc "Làm ngay"); `done` / `failed` = đã chạy.
 */
export const OffboardingHandoverStatus = z.enum(['unplanned', 'planned', 'done', 'failed']);
const Person = z.object({ id: z.uuid(), name: z.string() });
export const OffboardingHandoverItem = z.object({
    category: OffboardingCategory,
    /** Tệp: số tệp; người dưới quyền: số người. */
    count: z.number().int().nonnegative(),
    /** Chỉ nhóm tệp: tổng dung lượng, kể cả bản cũ. */
    bytes: z.number().int().nonnegative().nullable(),
    /** Chỉ nhóm người dưới quyền: danh sách tên. */
    people: z.array(Person),
    status: OffboardingHandoverStatus,
    /** `null` khi `planned`/`done` = quyết **không bàn giao** nhóm này. */
    target: Person.nullable(),
    plannedBy: Person.nullable(),
    plannedAt: z.iso.datetime().nullable(),
    executedAt: z.iso.datetime().nullable(),
    /** Kết quả lượt chạy (đếm đã chuyển, lý do lỗi) — dạng tự do do lệnh chạy ghi. */
    result: z.record(z.string(), z.unknown()).nullable(),
});
export const OffboardingHandoverView = z.object({
    user: z.object({
        id: z.uuid(),
        name: z.string(),
        lastWorkingDay: CalendarDate,
        /** Đã qua ngày cuối và tài khoản đã khoá. */
        locked: z.boolean(),
    }),
    /** Người xem đứng ở đây với tư cách nào — leader chỉ giao được trong team mình. */
    viewerRole: z.enum(['leader', 'admin']),
    items: z.array(OffboardingHandoverItem),
    /**
     * Người nhận chọn được. `canManage` = đủ vai làm quản lý trực tiếp (Trưởng nhóm
     * trở lên hoặc quản trị) — điều kiện của nhóm Người dưới quyền.
     */
    candidates: z.array(Person.extend({ isSelf: z.boolean(), canManage: z.boolean() })),
});
export const SaveOffboardingHandoverInput = z.object({
    items: z
        .array(z.object({
        category: OffboardingCategory,
        /** `null` = không bàn giao nhóm này. */
        targetUserId: z.uuid().nullable(),
    }))
        .min(1)
        .max(OFFBOARDING_CATEGORIES.length),
});
/** Một người nghỉ mà **mình** có phần tiếp quản — khối "Cần bàn giao" ở trang chủ. */
export const MyOffboardingHandover = z.object({
    userId: z.uuid(),
    name: z.string(),
    lastWorkingDay: CalendarDate,
    /** Số nhóm có dữ liệu mà chưa ai lên phương án. */
    unplanned: z.number().int().nonnegative(),
});
//# sourceMappingURL=offboarding.js.map